import { getActiveTenantId, getAuthGeneration, sparkOneAuthChangedEvent } from '@/src/auth/token-storage';
import { logger } from '@/src/logging/logger';
import {
  getBrowserVoiceObservabilityConfig,
  postBrowserVoiceSignalingEvents,
  type BrowserVoiceObservabilityConfig,
  type BrowserVoiceObservabilityMode,
} from './browser-voice-observability-api';

export type BrowserVoiceEventName =
  | 'browser_voice.managed_caller.prepare_started'
  | 'browser_voice.managed_caller.prepare_succeeded'
  | 'browser_voice.managed_caller.prepare_failed'
  | 'browser_voice.managed_caller.armed'
  | 'browser_voice.managed_caller.disarmed'
  | 'browser_voice.managed_caller.dispatch_started'
  | 'browser_voice.managed_caller.dispatch_failed'
  | 'browser_voice.registration.ua_start_invoked'
  | 'browser_voice.registration.observation_sent'
  | 'browser_voice.registration.observation_accepted'
  | 'browser_voice.registration.observation_failed'
  | 'browser_voice.registration.authoritative_socket_received'
  | 'browser_voice.registration.ready'
  | 'browser_voice.registration.disconnected'
  | 'browser_voice.registration.authority_revoked'
  | 'browser_voice.registration.ensure_started'
  | 'browser_voice.registration.retry_authorized'
  | 'browser_voice.registration.credentials_delivered'
  | 'browser_voice.registration.local_registered'
  | 'browser_voice.registration.authoritative_ready'
  | 'browser_voice.registration.public_ready'
  | 'browser_voice.sip.offer_received'
  | 'browser_voice.sip.transport_message_received'
  | 'browser_voice.sip.transport_invite_observed'
  | 'browser_voice.sip.invite_callback_invoked'
  | 'browser_voice.sip.invite_callback_timeout'
  | 'browser_voice.sip.invite_rejected_before_callback'
  | 'browser_voice.sip.offer_rejected_authority'
  | 'browser_voice.sip.correlation_present'
  | 'browser_voice.sip.correlation_missing'
  | 'browser_voice.sip.correlation_matched'
  | 'browser_voice.sip.correlation_mismatch'
  | 'browser_voice.sip.correlation_conflict'
  | 'browser_voice.auto_answer.started'
  | 'browser_voice.auto_answer.answer_invoked'
  | 'browser_voice.auto_answer.failed'
  | 'browser_voice.remote_playback.blocked'
  | 'browser_voice.sip.session_confirmed'
  | 'browser_voice.sip.session_failed'
  | 'browser_voice.sip.session_ended';

export type BrowserVoicePhase =
  | 'prepare' | 'arm' | 'dispatch' | 'registration' | 'offer'
  | 'correlation' | 'auto_answer' | 'remote_playback' | 'session';
export type BrowserVoiceReasonCode =
  | 'runtime_not_ready' | 'runtime_not_owner' | 'offer_authority_revoked'
  | 'registration_not_ready' | 'media_preflight_failed' | 'media_track_not_live'
  | 'microphone_denied' | 'invite_correlation_missing' | 'invite_correlation_mismatch'
  | 'invite_correlation_conflict' | 'managed_call_not_armed' | 'managed_call_terminal'
  | 'answer_threw' | 'session_failed_before_confirmed' | 'wss_disconnected'
  | 'config_unavailable' | 'telemetry_rate_limited' | 'invite_rejected_before_callback'
  | 'remote_playback_blocked';
export type BrowserVoiceSeverity = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export type BrowserVoiceSignalingEventV1 = {
  schemaVersion: 1;
  eventName: BrowserVoiceEventName;
  severity: BrowserVoiceSeverity;
  occurredAt: string;
  releaseSha: string;
  route: string;
  callSessionId?: string;
  sipCorrelationId?: string;
  browserSessionId?: string;
  browserDeviceInstanceId?: string;
  phase: BrowserVoicePhase;
  outcome?: 'started' | 'succeeded' | 'failed' | 'rejected' | 'timed_out' | 'ended';
  reasonCode?: BrowserVoiceReasonCode;
  elapsedMs?: number;
  /** Fixed allowlist; no SIP response text, headers, or payload is retained. */
  sipResponseStatus?: 400 | 415 | 416 | 482 | 488 | 500;
  attempt?: number;
  dialogDirection?: 'incoming' | 'outgoing';
  registrationState?: 'registering' | 'registered' | 'disconnected' | 'revoked';
  authorityPath?: 'provider_event' | 'verified_hint' | 'provider_snapshot' | 'recovery_read';
  offerAuthority?: 'accepting' | 'revoked';
  connectedDialogPreserved?: boolean;
  correlationState?: 'present' | 'missing' | 'matched' | 'mismatched' | 'conflict';
  mediaState?: 'not_started' | 'requesting' | 'ready' | 'blocked' | 'ended';
};

type EventInput = Omit<BrowserVoiceSignalingEventV1, 'schemaVersion' | 'occurredAt' | 'releaseSha' | 'route'> & Partial<Pick<BrowserVoiceSignalingEventV1, 'occurredAt' | 'releaseSha' | 'route'>>;
type Dependencies = {
  getConfig?: () => Promise<BrowserVoiceObservabilityConfig>;
  post?: (events: readonly BrowserVoiceSignalingEventV1[], keepalive: boolean) => Promise<void>;
  now?: () => Date;
  timer?: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (timer: ReturnType<typeof setTimeout>) => void;
  environment?: () => string;
  route?: () => string;
  releaseSha?: () => string;
  identity?: () => string;
};

const MAX_PENDING_EVENTS = 100;
const MAX_PENDING_BYTES = 64 * 1024;
const MAX_BATCH_EVENTS = 20;
const MAX_BATCH_BYTES = 32 * 1024;
const VERBOSE_CALL_LIMIT = 60;
const ESSENTIAL_CALL_LIMIT = 20;
const DEDUPE_WINDOW_MS = 2_000;
const FLUSH_DELAY_MS = 1_000;
const MAX_CONFIG_TTL_SECONDS = 3_600;
const ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const CALL_ID_PATTERN = /^call_[A-Za-z0-9_-]{8,128}$/;
const SIP_CORRELATION_PATTERN = /^svp1\.[A-Za-z0-9_-]{22,128}$/;
const essentialPositiveEvents = new Set<BrowserVoiceEventName>([
  'browser_voice.managed_caller.prepare_succeeded',
  'browser_voice.registration.ua_start_invoked',
  'browser_voice.registration.observation_sent',
  'browser_voice.registration.observation_accepted',
  'browser_voice.registration.authoritative_socket_received',
  'browser_voice.registration.ensure_started',
  'browser_voice.registration.retry_authorized',
  'browser_voice.registration.credentials_delivered',
  'browser_voice.registration.local_registered',
  'browser_voice.registration.authoritative_ready',
  'browser_voice.registration.public_ready',
  'browser_voice.managed_caller.armed',
  'browser_voice.managed_caller.dispatch_started',
  'browser_voice.sip.offer_received',
  'browser_voice.sip.transport_invite_observed',
  'browser_voice.sip.invite_callback_invoked',
  'browser_voice.sip.correlation_matched',
  'browser_voice.auto_answer.answer_invoked',
  'browser_voice.sip.session_confirmed',
]);
const terminalEvents = new Set<BrowserVoiceEventName>([
  'browser_voice.sip.session_confirmed',
  'browser_voice.sip.session_failed',
  'browser_voice.sip.session_ended',
  'browser_voice.managed_caller.dispatch_failed',
  'browser_voice.auto_answer.failed',
]);

function byteLength(value: unknown): number {
  const text = JSON.stringify(value);
  return typeof TextEncoder === 'undefined' ? text.length : new TextEncoder().encode(text).byteLength;
}
function cleanRoute(value: string): string { return value.split(/[?#]/, 1)[0].slice(0, 240) || '/'; }
function validOptional(value: string | undefined, pattern: RegExp): string | undefined { return value && pattern.test(value) ? value : undefined; }
function fallbackMode(environment: string): BrowserVoiceObservabilityMode { return environment === 'development' ? 'verbose' : 'essential'; }
function isFailure(event: BrowserVoiceSignalingEventV1): boolean {
  return event.severity === 'WARN' || event.severity === 'ERROR' || event.outcome === 'failed' || event.outcome === 'rejected' || event.outcome === 'timed_out';
}

export function createBrowserVoiceObservability(dependencies: Dependencies = {}) {
  const now = dependencies.now ?? (() => new Date());
  const environment = dependencies.environment ?? (() => process.env.NODE_ENV ?? 'development');
  const route = dependencies.route ?? (() => typeof window === 'undefined' ? '/' : window.location.pathname);
  const releaseSha = dependencies.releaseSha ?? (() => process.env.NEXT_PUBLIC_SPARKONE_RELEASE_SHA ?? '');
  const identity = dependencies.identity ?? (() => `${getAuthGeneration()}:${getActiveTenantId() ?? 'none'}`);
  const getConfig = dependencies.getConfig ?? getBrowserVoiceObservabilityConfig;
  const post = dependencies.post ?? postBrowserVoiceSignalingEvents;
  const timer = dependencies.timer ?? ((callback, delay) => setTimeout(callback, delay));
  const clearTimer = dependencies.clearTimer ?? clearTimeout;
  let policy: { mode: BrowserVoiceObservabilityMode; expiresAt: number } | null = null;
  let queue: BrowserVoiceSignalingEventV1[] = [];
  let queueBytes = 0;
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  let flushing = false;
  let configRequestIdentity: string | null = null;
  let activeIdentity = identity();
  const dedupe = new Map<string, number>();
  const callCounts = new Map<string, number>();

  const clear = () => {
    queue = [];
    queueBytes = 0;
    dedupe.clear();
    callCounts.clear();
    if (flushTimer) clearTimer(flushTimer);
    flushTimer = null;
  };
  const ensureIdentity = () => {
    const nextIdentity = identity();
    if (nextIdentity !== activeIdentity) { activeIdentity = nextIdentity; clear(); policy = null; configRequestIdentity = null; }
  };
  const resolvePolicy = () => {
    const fallback = fallbackMode(environment());
    if (policy && policy.expiresAt > now().getTime()) return policy.mode;
    const requestIdentity = activeIdentity;
    if (configRequestIdentity === requestIdentity) return fallback;
    configRequestIdentity = requestIdentity;
    void getConfig().then((config) => {
      if (config.schemaVersion !== 1 || !['off', 'essential', 'verbose'].includes(config.mode) || !Number.isInteger(config.configTtlSeconds) || config.configTtlSeconds < 1) {
        throw new Error('Voice observability config is malformed.');
      }
      if (requestIdentity !== activeIdentity || identity() !== requestIdentity) return;
      policy = { mode: config.mode, expiresAt: now().getTime() + Math.min(config.configTtlSeconds, MAX_CONFIG_TTL_SECONDS) * 1_000 };
      configRequestIdentity = null;
    }).catch(() => {
      if (requestIdentity !== activeIdentity || identity() !== requestIdentity) return;
      policy = { mode: fallback, expiresAt: now().getTime() + 60_000 };
      configRequestIdentity = null;
      logger.warn('sparkone.browser_voice.observability_config_unavailable', { reasonCode: 'config_unavailable' });
    });
    return fallback;
  };
  const flush = async (keepalive = false) => {
    ensureIdentity();
    if (flushing || queue.length === 0) return;
    flushing = true;
    if (flushTimer) clearTimer(flushTimer);
    flushTimer = null;
    const batch: BrowserVoiceSignalingEventV1[] = [];
    let bytes = byteLength({ events: batch });
    while (queue.length && batch.length < MAX_BATCH_EVENTS) {
      const candidate = queue[0];
      const candidateBytes = byteLength(candidate);
      if (batch.length && bytes + candidateBytes > MAX_BATCH_BYTES) break;
      queue.shift(); queueBytes -= candidateBytes; batch.push(candidate); bytes += candidateBytes;
    }
    try { await post(batch, keepalive); }
    catch { logger.warn('sparkone.browser_voice.observability_delivery_failed', { eventCount: batch.length }); }
    finally {
      flushing = false;
      if (queue.length) void flush(false);
    }
  };
  const scheduleFlush = () => {
    if (flushTimer) return;
    flushTimer = timer(() => { flushTimer = null; void flush(false); }, FLUSH_DELAY_MS);
  };
  const emit = (input: EventInput) => {
    ensureIdentity();
    const event: BrowserVoiceSignalingEventV1 = {
      schemaVersion: 1, eventName: input.eventName, severity: input.severity, phase: input.phase,
      occurredAt: input.occurredAt ?? now().toISOString(), releaseSha: (input.releaseSha ?? releaseSha()).slice(0, 64), route: cleanRoute(input.route ?? route()),
      ...(validOptional(input.callSessionId, CALL_ID_PATTERN) ? { callSessionId: input.callSessionId } : {}),
      ...(validOptional(input.sipCorrelationId, SIP_CORRELATION_PATTERN) ? { sipCorrelationId: input.sipCorrelationId } : {}),
      ...(validOptional(input.browserSessionId, ID_PATTERN) ? { browserSessionId: input.browserSessionId } : {}),
      ...(validOptional(input.browserDeviceInstanceId, ID_PATTERN) ? { browserDeviceInstanceId: input.browserDeviceInstanceId } : {}),
      ...(input.outcome ? { outcome: input.outcome } : {}), ...(input.reasonCode ? { reasonCode: input.reasonCode } : {}),
      ...(input.elapsedMs !== undefined ? { elapsedMs: Math.max(0, Math.min(300_000, Math.floor(input.elapsedMs))) } : {}),
      ...(input.sipResponseStatus !== undefined && [400, 415, 416, 482, 488, 500].includes(input.sipResponseStatus) ? { sipResponseStatus: input.sipResponseStatus } : {}),
      ...(input.attempt !== undefined ? { attempt: Math.max(0, Math.min(20, Math.floor(input.attempt))) } : {}),
      ...(input.dialogDirection ? { dialogDirection: input.dialogDirection } : {}), ...(input.registrationState ? { registrationState: input.registrationState } : {}),
      ...(input.authorityPath ? { authorityPath: input.authorityPath } : {}),
      ...(input.offerAuthority ? { offerAuthority: input.offerAuthority } : {}), ...(input.connectedDialogPreserved !== undefined ? { connectedDialogPreserved: input.connectedDialogPreserved } : {}), ...(input.correlationState ? { correlationState: input.correlationState } : {}), ...(input.mediaState ? { mediaState: input.mediaState } : {}),
    };
    // Console output is intentionally identifier-free. Raw call/session/device and
    // SIP-correlation IDs are only retained in the validated HTTP envelope.
    const consoleContext = { eventName: event.eventName, phase: event.phase, outcome: event.outcome, reasonCode: event.reasonCode, elapsedMs: event.elapsedMs, sipResponseStatus: event.sipResponseStatus, correlationState: event.correlationState };
    if (event.severity === 'ERROR') {
      logger.error('sparkone.browser_voice.signaling', undefined, consoleContext);
    } else if (event.severity === 'WARN') {
      logger.warn('sparkone.browser_voice.signaling', consoleContext);
    } else if (event.severity === 'INFO') {
      logger.info('sparkone.browser_voice.signaling', consoleContext);
    } else {
      logger.debug('sparkone.browser_voice.signaling', consoleContext);
    }
    // Unit-test runs exercise call control without an authenticated telemetry
    // backend. Keep that environment console-only so diagnostics never alter
    // mocked request ordering or leave background network work behind.
    if (environment() === 'test') return;
    const mode = resolvePolicy();
    if (mode === 'off' || (mode === 'essential' && !isFailure(event) && !essentialPositiveEvents.has(event.eventName))) return;
    const key = [event.eventName, event.callSessionId ?? '', event.sipCorrelationId ?? '', event.phase, event.outcome ?? '', event.reasonCode ?? ''].join('|');
    const observedAt = now().getTime();
    if ((dedupe.get(key) ?? 0) + DEDUPE_WINDOW_MS > observedAt) return;
    dedupe.set(key, observedAt);
    const callKey = event.callSessionId ?? '__unscoped__';
    const count = callCounts.get(callKey) ?? 0;
    if (count >= (mode === 'verbose' ? VERBOSE_CALL_LIMIT : ESSENTIAL_CALL_LIMIT) && !isFailure(event)) return;
    callCounts.set(callKey, count + 1);
    const size = byteLength(event);
    if (size > MAX_BATCH_BYTES || queue.length >= MAX_PENDING_EVENTS || queueBytes + size > MAX_PENDING_BYTES) {
      logger.warn('sparkone.browser_voice.observability_queue_dropped', { reasonCode: 'telemetry_rate_limited' });
      return;
    }
    queue.push(event); queueBytes += size;
    if (terminalEvents.has(event.eventName) || queue.length >= MAX_BATCH_EVENTS) void flush(true); else scheduleFlush();
  };
  const onPageHidden = () => { if (typeof document !== 'undefined' && document.visibilityState === 'hidden') void flush(true); };
  if (typeof window !== 'undefined') {
    window.addEventListener(sparkOneAuthChangedEvent, ensureIdentity);
    window.addEventListener('sparkone:auth-lost', clear);
    document.addEventListener('visibilitychange', onPageHidden);
  }
  return { emit, flush, clear, getPendingCount: () => queue.length };
}

export const browserVoiceObservability = createBrowserVoiceObservability();
export function emitBrowserVoiceSignalingEvent(event: EventInput) { browserVoiceObservability.emit(event); }
